import { useEffect, useState } from 'react';
import { CopyableEmail } from "@/components/common/CopyableEmail";
import { ConversationMessage, SetupAnalysisResult } from '@/types/agent/setup';

interface ConfigurationPanelProps {
  analysisResult: SetupAnalysisResult | null;
  messages: ConversationMessage[];
  isAnalyzing: boolean;
  interfaceMode: "voice" | "text";
  connected?: boolean;
  setupPhase?: "conversation" | "analysis" | "confirmation" | "complete";
  onGuideVisibilityChange?: (visible: boolean) => void;
}

export const ConfigurationPanel = ({
  analysisResult,
  messages,
  isAnalyzing,
  interfaceMode,
  connected = true,
  setupPhase = "conversation",
  onGuideVisibilityChange,
}: ConfigurationPanelProps) => {
  const [showPanel, setShowPanel] = useState(false);
  const [visibleSteps, setVisibleSteps] = useState(0);

  useEffect(() => {
    // Show panel after a short delay during conversation; otherwise immediately
    if (setupPhase === "conversation" && !analysisResult) {
      const timer = setTimeout(() => {
        setShowPanel(true);
        onGuideVisibilityChange?.(true);
      }, 1000);

      return () => {
        clearTimeout(timer);
        onGuideVisibilityChange?.(false);
      };
    } else {
      setShowPanel(true);
    }
  }, [setupPhase, analysisResult, onGuideVisibilityChange]);

  // Sequentially reveal the 7 setup steps for clarity
  useEffect(() => {
    if (setupPhase === "conversation" && !analysisResult && showPanel) {
      setVisibleSteps(0);
      let step = 0;
      const interval = setInterval(() => {
        step += 1;
        setVisibleSteps(step);
        if (step >= 7) {
          clearInterval(interval);
        }
      }, 180);
      return () => clearInterval(interval);
    }
  }, [setupPhase, analysisResult, showPanel]);
  const getTitle = () => {
    if (setupPhase === "conversation") {
      return "Genie Setup Guide";
    }
    return "Genie Configuration";
  };

  const getSubtitle = () => {
    if (setupPhase === "conversation") {
      return interfaceMode === "text"
        ? "Here are the key details we'll collect during your chat"
        : "Here are the key details we'll collect during your conversation";
    }
    return interfaceMode === "voice"
      ? "Parameters will be extracted after you end the conversation"
      : "Parameters will be extracted when you end the chat";
  };

  // Initial guidance content before conversation starts
  const renderInitialGuidance = () => (
    <div className="space-y-4 md:space-y-5">
      {/* Gradient border wrapper with fade-in */}
      <div
        className={`rounded-xl p-[1px] bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] transition-opacity duration-700 ${
          showPanel ? "opacity-100" : "opacity-0"
        }`}
      >
        <div className="bg-white rounded-[11px] p-5 md:p-8">
          <h4 className="text-lg md:text-xl font-semibold text-genie-primary mb-3 md:mb-4">Genie Setup Roadmap</h4>
          <p className="text-sm md:text-sm text-blue-800 mb-4 md:mb-6">We'll guide you through seven clear steps:</p>

          <ol className="space-y-3 md:space-y-4">
            {[
              {
                title: "Genie Name",
                desc: "What should we call your Genie?",
                guidance:
                  "Keep it short, easy to say out loud, and memorable. Your Genie will introduce itself with this name every time, so pick something that fits your brand.",
                example: "A person's name, your business name, or a role like Concierge, Assistant, or Helper all work.",
              },
              {
                title: "Purpose / Use Case",
                desc: "What problem does it solve for users?",
                guidance:
                  "Describe who talks to your Genie, what they want, and what a great outcome looks like. The clearer this is, the better your Genie handles edge cases later.",
                example:
                  "Think of the questions your team answers over and over. Pricing, hours, availability, how something works, where to find something. That is the job.",
              },
              {
                title: "Opening Greeting",
                desc: "How should your Genie introduce itself?",
                guidance:
                  "The first thing every caller hears. Open warm, identify the business or the Genie, and end with an invitation to talk. Keep it under 15 seconds spoken.",
                example: "e.g. \"Hi, you've reached [Business Name]. What can I help you with today?\"",
              },
              {
                title: "Persona & Tone",
                desc: "Set the style: friendly, professional, playful, etc.",
                guidance:
                  "Think about how your best team member sounds when they pick up the phone. Pick 2 or 3 adjectives that describe how the Genie should come across on every interaction.",
                example: "e.g. Friendly, calm, professional. Or playful, casual, energetic. Match it to your brand.",
              },
              {
                title: "Language & Voice",
                desc: "Primary language and voice preference.",
                guidance:
                  "Choose the primary language your Genie speaks first. It can still respond in 30+ languages if someone switches. Pick a voice that matches the persona and your brand.",
                example: "e.g. English (NZ), warm female voice. Or Spanish (LatAm), friendly male voice.",
              },
              {
                title: "Master Prompt",
                desc: "The core system prompt that shapes behavior.",
                guidance:
                  "This is the most important field. It tells the Genie who it is, what it knows, what it should never do, and when to hand off to a human. Treat it like a job description for a new team member on day one.",
                example:
                  "Cover four things: 1) who the Genie is and who it works for, 2) the topics it can help with, 3) what it should never do (e.g. quote prices that are not in the knowledge base, make promises on availability), and 4) what to do when something is out of scope (take details, escalate to a human).",
              },
              {
                title: "Support Contacts",
                desc: "Emails for reports and handoffs (optional).",
                guidance:
                  "Email addresses for the people who should see call summaries, lead handoffs, and escalations. Add anyone who needs visibility into what the Genie is doing.",
                example: "e.g. owner@yourbiz.com, sales@yourbiz.com, support@yourbiz.com",
              },
            ].map((step, index) => (
              <li
                key={step.title}
                className={`flex items-start gap-3 md:gap-4 transition-all duration-500 ${
                  visibleSteps >= index + 1 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-1"
                }`}
              >
                <div className="w-7 h-7 md:w-9 md:h-9 rounded-full bg-genie-primary text-white flex items-center justify-center font-bold text-sm md:text-base flex-shrink-0">
                  {index + 1}
                </div>
                <div className="flex-1">
                  <div className="text-sm md:text-base font-semibold text-gray-900">{step.title}</div>
                  <div className="text-xs md:text-xs text-gray-700">{step.desc}</div>
                  <details className="mt-2">
                    <summary
                      className="text-xs md:text-xs text-genie-primary cursor-pointer select-none"
                      aria-label={`More guidance for ${step.title}`}
                    >
                      More guidance
                    </summary>
                    <div className="mt-1 md:mt-2 space-y-1.5 text-xs md:text-xs">
                      <p className="text-blue-900/90 leading-relaxed">{step.guidance}</p>
                      {step.example && (
                        <p className="text-gray-600 italic leading-relaxed">{step.example}</p>
                      )}
                    </div>
                  </details>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
        <p className="text-base md:text-base text-gray-700 text-center">
          {interfaceMode === "text" ? (
            <>
              Start typing in the chat to begin step 1.
              <br />
              We'll capture each step in order — 1 → 7 — as you chat.
            </>
          ) : (
            <>
              <span className="md:hidden">
                Tap the <strong className="text-genie-primary">Genie Avatar</strong> to begin step 1.
              </span>
              <span className="hidden md:inline">
                Click <strong className="text-genie-primary">"Begin Setup"</strong> or the{" "}
                <strong className="text-genie-primary">Genie Avatar</strong> to start.
                <br />
                We'll capture each step in order — 1 → 7 — as you talk.
              </span>
            </>
          )}
        </p>
        <p className="mt-2 text-xs md:text-xs text-gray-500 text-center">
          You can end the {interfaceMode === "text" ? "chat" : "conversation"} anytime and refine details later.
        </p>
      </div>
    </div>
  );

  // Content during conversation
  const renderConversationContent = () => (
    <div className="space-y-4 md:space-y-5">
      {/* Conversation status card with gradient border */}
      <div
        className={`rounded-lg p-[1px] bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] transition-opacity duration-700 ${
          showPanel ? "opacity-100" : "opacity-0"
        }`}
      >
        <div className="bg-white rounded-md p-4 md:p-5">
          <p className="text-base md:text-lg text-genie-primary">
            <strong>{interfaceMode === "text" ? "Chat in progress..." : "Conversation in progress..."}</strong>
            {interfaceMode === "text" ? (
              <>
                <span className="md:inline hidden">
                  {" "}
                  Keep chatting about your requirements. When you're ready, click <strong>"End Chat & Analyze"</strong>{" "}
                  to extract the configuration.
                </span>
                <span className="md:hidden"> Tap "End Chat & Analyze" when ready.</span>
              </>
            ) : (
              <>
                <span className="md:inline hidden">
                  {" "}
                  Keep talking with the Genie about your requirements. When you're ready, click the <strong>"End Setup & Analyze"</strong>{" "}
                  button to extract the configuration.
                </span>
                <span className="md:hidden"> Tap "End Setup & Analyze" when ready.</span>
              </>
            )}
          </p>
        </div>
      </div>

      <div className="space-y-3 md:space-y-4">
        <div className="rounded-lg p-[1px] bg-gradient-to-r from-blue-200 to-indigo-200 mb-3 md:mb-4">
          <div className="bg-white rounded-md p-3">
            <h5 className="text-sm md:text-base font-semibold text-blue-700 mb-1">Primary Focus</h5>
            <p className="text-xs md:text-sm text-blue-600">Core details we're collecting first</p>
          </div>
        </div>

        <div className="rounded-lg p-[1px] bg-gradient-to-r from-[#E5E7EB] to-[#E5E7EB]">
          <div className="bg-white rounded-md p-4 md:p-5">
            <h5 className="text-sm md:text-base font-semibold text-gray-700 mb-2 md:mb-3">Parameters Being Extracted</h5>
            <p className="text-xs md:text-sm text-gray-600 mb-2 md:mb-3">
              These details will be extracted after {interfaceMode === "text" ? "chat" : "conversation"} ends:
            </p>
            <div className="space-y-2 md:space-y-2.5">
              {["Genie Name", "Purpose / Description", "Opening Greeting", "Persona & Tone", "Languages", "Master Prompt"].map((label) => (
                <div key={label} className="flex items-center gap-2">
                  <span className="w-2 h-2 md:w-2.5 md:h-2.5 bg-genie-primary rounded-full flex-shrink-0 animate-pulse" />
                  <span className="text-xs md:text-sm text-gray-700">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="rounded-lg p-[1px] bg-gradient-to-r from-[#E5E7EB] to-[#E5E7EB] mt-3 md:mt-4">
          <div className="bg-white rounded-md p-3">
            <h5 className="text-xs md:text-sm font-medium text-gray-600 mb-1">Secondary Parameters</h5>
            <p className="text-xs text-gray-500">Auxiliary details collected naturally when mentioned</p>
          </div>
        </div>
      </div>
    </div>
  );

  // Content showing extracted results
  const renderExtractedResults = () => (
    <div className="space-y-3 md:space-y-4">
      <div className="bg-blue-50 border border-genie-primary rounded-lg p-3 md:p-4">
        <p className="text-sm md:text-base text-genie-primary">
          <strong>Analysis Complete!</strong> Here are the details we extracted from your conversation:
        </p>
      </div>

      <div className="space-y-3 md:space-y-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 md:p-3 mb-3 md:mb-4">
          <h5 className="text-xs md:text-sm font-semibold text-blue-700 mb-1">Primary Parameters</h5>
          <p className="text-xs text-blue-600">Core details captured from your conversation</p>
        </div>

        <div>
          <label className="block text-xs md:text-base font-medium text-gray-700 mb-1">Genie Name</label>
          <div className="p-2 md:p-3 bg-white border border-gray-200 rounded-md text-xs md:text-base font-medium text-gray-800">
            {analysisResult?.genieName || "Not extracted"}
          </div>
        </div>

        <div>
          <label className="block text-xs md:text-base font-medium text-gray-700 mb-1">Purpose / Description</label>
          <div className="p-2 md:p-3 bg-white border border-gray-200 rounded-md text-xs md:text-base text-gray-700">
            {analysisResult?.useCase || "Not extracted"}
          </div>
        </div>

        <div>
          <label className="block text-xs md:text-base font-medium text-gray-700 mb-1">Opening Greeting</label>
          <div className="p-2 md:p-3 bg-white border border-gray-200 rounded-md text-xs md:text-base text-gray-700">
            {analysisResult?.firstMessage || "Not extracted"}
          </div>
        </div>

        <div>
          <label className="block text-xs md:text-base font-medium text-gray-700 mb-1">Persona & Tone</label>
          <div className="p-2 md:p-3 bg-white border border-gray-200 rounded-md text-xs md:text-base text-gray-700">
            {analysisResult?.voicePreference || "Not extracted"}
          </div>
        </div>

        <div>
          <label className="block text-xs md:text-base font-medium text-gray-700 mb-1">Language</label>
          <div className="p-2 md:p-3 bg-white border border-gray-200 rounded-md text-xs md:text-base text-gray-700">
            {analysisResult?.language || "Not extracted"}
          </div>
        </div>

        <div>
          <label className="block text-xs md:text-base font-medium text-gray-700 mb-1">Master Prompt</label>
          <div className="p-2 md:p-3 bg-white border border-gray-200 rounded-md text-xs md:text-base text-gray-700 max-h-24 md:max-h-32 overflow-y-auto">
            {analysisResult?.systemPrompt || "Not extracted"}
          </div>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-2 md:p-3 mt-4 md:mt-6">
          <h5 className="text-xs md:text-sm font-semibold text-gray-700 mb-1 md:mb-2">Secondary Parameters</h5>
          <p className="text-xs text-gray-600 mb-2 md:mb-3">Auxiliary details collected naturally during conversation</p>

          <div className="space-y-2 md:space-y-3">
            {analysisResult?.brand && (
              <div>
                <span className="text-xs md:text-sm font-medium text-gray-600">Brand: </span>
                <span className="text-xs md:text-sm text-gray-700">{analysisResult.brand}</span>
              </div>
            )}

            {analysisResult?.category && (
              <div>
                <span className="text-xs md:text-sm font-medium text-gray-600">Category: </span>
                <span className="text-xs md:text-sm text-gray-700">{analysisResult.category}</span>
              </div>
            )}

            {analysisResult?.supportEmails && analysisResult.supportEmails.length > 0 && (
              <div>
                <span className="text-xs md:text-sm font-medium text-gray-600">Support Contacts: </span>
                <span className="text-xs md:text-sm text-gray-700">
                  {analysisResult.supportEmails.map((email, index) => (
                    <CopyableEmail key={`${email}-${index}`} email={email} className="mr-2" />
                  ))}
                </span>
              </div>
            )}

            {analysisResult?.conversationSettings && (
              <div>
                <span className="text-xs md:text-sm font-medium text-gray-600">Conversation Settings: </span>
                <span className="text-xs md:text-sm text-gray-700">
                  Turn timeout: {analysisResult.conversationSettings.turnTimeout}s, Silence timeout:{" "}
                  {analysisResult.conversationSettings.silenceTimeout}s
                </span>
              </div>
            )}

            {!analysisResult?.brand &&
              !analysisResult?.category &&
              (!analysisResult?.supportEmails || analysisResult.supportEmails.length === 0) && (
                <p className="text-xs text-gray-500 italic">No auxiliary parameters detected in conversation</p>
              )}
          </div>
        </div>

        {analysisResult?.confidence && (
          <div className="mt-3 md:mt-4">
            <label className="block text-xs md:text-base font-medium text-gray-700 mb-1">Extraction Confidence</label>
            <div className="p-2 md:p-3 bg-white border border-gray-200 rounded-md">
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-gray-200 rounded-full h-1.5 md:h-2">
                  <div
                    className="bg-genie-primary h-1.5 md:h-2 rounded-full transition-all duration-300"
                    style={{ width: `${analysisResult.confidence * 100}%` }}
                  ></div>
                </div>
                <span className="text-xs md:text-base font-medium text-gray-700">{Math.round(analysisResult.confidence * 100)}%</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div
      className={`h-full bg-white border-l border-gray-200 flex flex-col ${
        showPanel ? "opacity-100" : "opacity-0"
      } transition-opacity duration-500`}
    >
      <div className="p-3 md:p-4 lg:p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg md:text-2xl font-semibold text-gray-800">{getTitle()}</h3>
            <p className="text-sm md:text-base text-gray-600 mt-0.5 md:mt-1">{getSubtitle()}</p>
          </div>
          {isAnalyzing && (
            <div className="flex items-center gap-1 md:gap-2 text-blue-600">
              <div className="animate-spin rounded-full h-3 w-3 md:h-4 md:w-4 border-b-2 border-blue-600"></div>
              <span className="text-xs font-medium">Analyzing...</span>
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-3 md:p-4 lg:p-6">
        {setupPhase === "conversation" && !analysisResult && renderInitialGuidance()}
        {(analysisResult || setupPhase === "confirmation") && renderExtractedResults()}
      </div>
    </div>
  );
};
